/**
 * A quote's figure as the person paying reads it — price before IVA, the IVA, and the total. `W4-T16`.
 *
 * Three labelled lines, a `<dl>`, so each number is announced with what it is. The stored figure is
 * the first line (ADR-017 §3.1: entered without IVA); the other two are computed here with the
 * contract's `withIva`, so the web and the API can never round the same quote two ways.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { withIva } from '@marketplace/contracts';

import { DISPLAY_IVA_RATE } from './price.js';

export function QuotePrice({
  amountCents,
  locale,
  rate = DISPLAY_IVA_RATE,
}: {
  amountCents: number;
  locale: string;
  rate?: number;
}): ReactElement {
  const { t } = useTranslation();
  const { iva, total } = withIva(amountCents, rate);
  const money = (cents: number): string =>
    (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });

  const lines: [string, number][] = [
    [t('quotePrice.beforeIva'), amountCents],
    [t('quotePrice.iva', { rate: rate.toLocaleString(locale, { style: 'percent' }) }), iva],
    [t('quotePrice.total'), total],
  ];

  return (
    <dl className="mp-meta" data-testid="quote-price">
      {lines.map(([label, cents]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{money(cents)}</dd>
        </div>
      ))}
    </dl>
  );
}
